import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { NgForOf, NgIf } from "@angular/common";
import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import { firstValueFrom, Subject } from "rxjs";
import { MetricDataRole, MetricDataRoleByNameMap } from "../../dto/MetricData";
import { UUID } from "../../../../core/pojo/id-variable";
import { MetricPeriodWrapper } from "../../dto/MetricGroupsAndPeriods";
import {
  MetricDataRoleForSelector,
  MetricPeriodMonth,
  MetricYearPlanning,
  MetricYearSelectionStatus,
  MetricYearsForAddPeriodSelector,
} from "../../dto/ManagePeriodsWrapper";
import { ModalButtons, SpinnerProps } from "../../../shared/component/modal-container/modal-container.component";
import { NOTIFICATIONS, NotificationsService } from "../../../../core/service/notifications.service";
import { KpiBusinessService } from "../../service/backend/kpi-business.service";
import { LocalDate } from "../../../../core/pojo/date-variable";
import { DateService } from "../../../../util/common/date-service/date.service";
import { getNaturalNumberArrayFromNTillM } from "../../../../util/common/math-util";
import { CheckBoxSelector } from "../../../shared/component/checkbox-selector/checkbox-selector.component";
import { MetricBulkRequestCompany } from "../../dto/metric-bulk-request";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-kpi-modal-delete-periods',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    SharedComponentsModule,
  ],
  templateUrl: './kpi-modal-delete-periods.component.html',
  styleUrl: './kpi-modal-delete-periods.component.scss',
})
export class KpiModalDeletePeriodsComponent implements OnChanges, OnDestroy {
  _destroyStream: Subject<void> = new Subject<void>();
  protected readonly MetricDataRoleMap = MetricDataRoleByNameMap;

  @Input({ required: true }) orgUUID: UUID;
  @Input({ required: true }) periods: MetricPeriodWrapper;
  @Input({ required: true }) companies: MetricBulkRequestCompany[];
  @Output() closeModal = new EventEmitter<boolean>();

  title: string;

  periodType: MetricDataRole = MetricDataRole.ACTUAL;
  periodTypeForSelector: MetricDataRoleForSelector;

  startingYears: MetricYearsForAddPeriodSelector;
  endingYears: MetricYearsForAddPeriodSelector;

  displayedPeriods: MetricYearPlanning[];
  periodsByType: LocalDate[];

  selected: MetricYearSelectionStatus = 'none';

  showPeriods: boolean;
  applyActivated = false;

  modalButtons: ModalButtons = {
    dismiss: 'Close',
  };

  spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  companiesForSelector: CheckBoxSelector[];
  selectedCompanies: CheckBoxSelector[] = [];

  constructor(
    private notificationService: NotificationsService,
    private kpiBusinessService: KpiBusinessService,
  ) {
  }

  ngOnChanges(): void {
    this.updateViewByPeriodType(this.periodType);
    this.prepareCompaniesForSelector();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  updateViewByPeriodType(periodType: MetricDataRole): void {
    this.updateTitle();
    this.resetData();
    this.setPeriodTypeForSelector(periodType);
    if (this.periodType === MetricDataRole.ACTUAL) {
      this.periodsByType = this.periods.actual.map((period) => period.period);
      if (!this.periods.actual.length) {
        return;
      }
    }
    if (this.periodType === MetricDataRole.BUDGET) {
      this.periodsByType = this.periods.budget.map((period) => period.period);
      if (!this.periods.budget.length) {
        return;
      }
    }
    this.prepareYears();
    this.showPeriods = true;
    this.modalButtons = {
      dismiss: 'Close',
      apply: 'Apply',
    };
  }

  async confirm(save: boolean): Promise<void> {
    if (!save) {
      this.closeModal.emit(false);
      return;
    }
    if (!this.applyActivated) {
      this.notificationService.warning(NOTIFICATIONS.METRICS_PERIODS_CREATE_WARNING);
      return;
    }
    const periods = this.preparePeriods();

    // We have a limitation (probably by AWS) to have limited amount of query parameters, which is why
    // we have to chunk our periods and make separate requests
    const chunkedPeriods: LocalDate[][] = [];
    const chunkSize = 30;
    for (let i = 0; i < periods.length; i += chunkSize) {
      chunkedPeriods.push(periods.slice(i, i + chunkSize));
    }

    for (let i = 0; i < this.selectedCompanies.length; i++) {
      for (const chunk of chunkedPeriods) {
        await firstValueFrom(this.kpiBusinessService.deletePeriods(chunk, this.selectedCompanies[i].UUID, this.periodType)
          .pipe(takeUntil(this._destroyStream)));
      }
      this.updateTitle((Math.floor((i / this.selectedCompanies.length) * 100)));
    }

    this.notificationService.success(NOTIFICATIONS.METRICS_PERIODS_DELETED_SUCCESS);
    this.closeModal.emit(true);
  }

  selectAll(selected: boolean): void {
    this.selected = selected ? 'all' : 'none';
    this.displayedPeriods.forEach((year) => {
      year.months.forEach((month) => {
        if (!month.disabled) {
          month.selected = selected;
        }
        year.selected = this.selected;
      });
    });

    this.setAllSelected();
  }

  /**
   * After selecting a year, we have a couple of options:
   * 1) nothing was selected => select quarters months.
   * 1a) if no quarter month is available, select all available months (e.x. 01 and 02 months).
   * 2) some was selected => select all remaining non-disabled months.
   * 3) all were selected => unselect all.
   * 4) all non-disabled were selected => unselect all.
   *
   * @param year with months
   * @param selected if the year should be selected.
   */
  selectYear(year: MetricYearPlanning, selected: boolean): void {
    const isAllSelected = year.months
      .filter((month) => !month.disabled)
      .every((month) => month.selected);
    const isSomeSelected = year.months.some((month) => month.selected);

    if (selected && !isSomeSelected) { // when no checkbox was selected -> select quarters
      const quarterMonths = [2, 5, 8, 11];
      const nonDisabledMonths = year.months
        .filter((month) => !month.disabled);
      const filteredByQuarterMonths = nonDisabledMonths
        .filter((month) => quarterMonths.includes(month.getNumber()));

      if (filteredByQuarterMonths.length) {
        filteredByQuarterMonths.forEach((month) => month.selected = selected);
      } else {
        nonDisabledMonths.forEach((month) => month.selected = selected);
      }
    } else {
      // when not all checkboxes were selected -> select all, otherwise unselect all.
      year.months
        .filter((month) => !month.disabled)
        .forEach((month) => month.selected = !isAllSelected);
    }

    this.checkYearIfAllMonthSelected(year);
  }

  selectMonth(month: MetricPeriodMonth, year: MetricYearPlanning, selected: boolean): void {
    month.selected = selected;
    this.checkYearIfAllMonthSelected(year);
  }

  /**
   * Initializes data based on the period type.
   * Sets the period type for the selector with appropriate name and styling.
   */
  setPeriodTypeForSelector(periodType: MetricDataRole): void {
    const style = periodType === MetricDataRole.ACTUAL ? 'main-8' : 'orange-11';
    this.periodTypeForSelector = {
      name: periodType,
      iconNameAndStylingLeft: `circle.svg; ${style}`,
    };
    this.periodType = periodType;
  }

  updateSelectedCompanies(companies: CheckBoxSelector[]): void {
    this.selectedCompanies = companies.filter((company) => company.chosen);
    this.checkApplyActivated();
  }

  private updateTitle(percentageOfUpdatedCompanies?: number): void {
    if (percentageOfUpdatedCompanies) {
      this.title = `Deleting periods: ${percentageOfUpdatedCompanies} out of 100%`;
    } else {
      this.title = 'Delete periods';
    }
  }

  /**
   * Updates the 'selected' status of a given year based on the selection status of its months.
   * There is a case, when all non-disabled are selected, so the checkbox should be selected as 'all'.
   * Calls 'setAllSelected' to update 'selected' status of the whole table.
   *
   * @param year - The year to check and update.
   */
  private checkYearIfAllMonthSelected(year: MetricYearPlanning): void {
    const isAllSelected = year.months
      .filter((month) => !month.disabled)
      .every((month) => month.selected);
    const isSomeSelected = year.months.some((month) => month.selected);

    this.checkApplyActivated();
    if (isAllSelected) {
      year.selected = 'all';
    } else {
      year.selected = isSomeSelected ? 'some' : 'none';
    }
    this.setAllSelected();
  }

  /**
   * Checks if any month in the displayed periods is selected and at least one company is selected.
   * Updates the 'applyActivated' flag to true if any selected month is found and selected companies has size > 0.
   * Assumes that periods from the database are disabled, and only enabled months are considered new.
   */
  private checkApplyActivated(): void {
    const isPeriodChosen = this.displayedPeriods.some((year) =>
      year.months.some((month) => month.selected && !month.disabled),
    );
    const isCompanySelected = this.selectedCompanies.length > 0;
    this.applyActivated = isCompanySelected && isPeriodChosen;
  }

  /**
   * Checks if all periods are selected and updates the `selected` property accordingly.
   * Calls `checkApplyActivated` to determine if there is at least one period to save.
   *
   * Note:
   * `checkApplyActivated` is called only from this method, as it is the final step in the selection status checking cycle.
   */
  private setAllSelected(): void {
    this.checkApplyActivated();
    if (this.displayedPeriods.every((year) => year.selected === 'all')) {
      this.selected = 'all';
      return;
    }
    const isSomeSelected = this.displayedPeriods.some((year) => year.selected === 'some' || year.selected === 'all');
    this.selected = isSomeSelected ? 'some' : 'none';
  }

  /**
   * Initializes the periods if the list of periods is empty.
   *
   * @param years - The range of years to initialize.
   */
  private initializePeriods(years: number[]): void {
    this.displayedPeriods = years.map((year) => new MetricYearPlanning(year));
    this.setDisabledPeriods();
  }

  private prepareYears(): void {
    const minYear = DateService.getYear(this.periodsByType[0]);
    const maxYear = DateService.getYear(this.periodsByType.at(-1));
    this.initializePeriods(getNaturalNumberArrayFromNTillM(Number(minYear), Number(maxYear)));
    this.startingYears = {
      all: [{ year: Number(minYear), disabled: false }],
      selected: { year: Number(minYear), disabled: false },
    };
    this.endingYears = {
      all: [{ year: Number(maxYear), disabled: false }],
      selected: { year: Number(maxYear), disabled: false },
    };
  }

  /**
   * Disable periods that are not persisted in the database.
   * Periods provided as input are considered deletable*.
   * * Note: This is not entirely accurate, as there are actual and budget periods.
   *   For simplicity, they are the same. Deleting a non-existent period will have no effect.
   */
  private setDisabledPeriods(): void {
    this.displayedPeriods.forEach((year) => {
      year.months.forEach((month) => {
        if (!this.periodsByType.some((period) => period.startsWith(`${year.year}-${month.getFullMonth()}`))) {
          month.disabled = true;
        }
      });
      year.allSelectedDisabled = year.months.every((month) => month.disabled);
    });

    this.setAllSelected();
  }

  /**
   * Prepares the list of companies for a checkbox selector.
   * This method initializes two arrays:
   * 1. `companiesForSelector`: A list of `CheckBoxSelector` objects representing companies available for selection.
   * 2. `selectedCompanies`: A list of pre-selected `CheckBoxSelector` objects,
   * which will include the company if there is only one in the input.
   */
  private prepareCompaniesForSelector(): void {
    this.companiesForSelector = [];
    this.selectedCompanies = [];
    // prepare companies for selector in case of calling modal from single company component
    if (this.companies.length === 1) {
      this.companiesForSelector.push(
        {
          UUID: this.companies[0].uuid,
          item: this.companies[0].name,
          chosen: true,
        }
      );
      this.selectedCompanies.push(this.companiesForSelector[0]);
      return;
    }

    // prepare companies for selector in case of calling modal from bulk request
    this.companies
      .filter((company) => company.isTracked)
      .forEach((company) => this.companiesForSelector.push(
        {
          UUID: company.uuid,
          item: company.name,
          chosen: false,
        }
      ));
  }


  /**
   * Prepares periods to be saved:
   * checks that period was selected in the modal and maps to the LocalDate format.
   *
   * @return parsed LocalDate periods.
   */
  private preparePeriods(): LocalDate[] {
    const newPeriods: LocalDate[] = [];
    this.displayedPeriods.forEach((year) => {
      year.months.forEach((month) => {
        if (month.selected && !month.disabled) {
          newPeriods.push(DateService.hyphenDate(year.year, month.getNumber() + 1, 1));
        }
      });
    });
    return newPeriods;
  }

  private resetData(): void {
    this.displayedPeriods = null;
    this.periodTypeForSelector = null;
    this.startingYears = { all: [], selected: null };
    this.endingYears = { all: [], selected: null };
    this.displayedPeriods = null;
    this.selected = 'none';
    this.showPeriods = null;
    this.modalButtons = {
      dismiss: 'Close',
    };
  }
}
